/**
 * Webhook do Make — envio do Lead com payload completo para CAPI.
 * ============================================================
 * Esta lib NÃO chama a Conversions API diretamente. Ela junta tudo
 * que o Meta espera no `user_data` + `custom_data` + `event_data` e
 * envia para um webhook do Make, que então faz a chamada server-side
 * para a Graph API. Dessa forma:
 *
 *   • O Access Token do Meta nunca aparece no client (fica no Make).
 *   • Funciona em SPA pura, sem precisar de backend próprio.
 *   • Sobrevive a adblockers/ITP/iOS, porque a chamada Graph API é
 *     feita de servidor do Make → Meta, não do navegador.
 *
 * Estratégia de envio (3 fallbacks em série):
 *   1. fetch keepalive (síncrono no thread, sobrevive a unload)
 *   2. navigator.sendBeacon (envia em background)
 *   3. Image beacon (gambiarra de última instância, GET com body em query)
 *
 * Logs sempre visíveis no console — prefixo [webhookLead]. Em produção
 * isso é proposital para facilitar debug em campanhas pagas.
 */

import { MAKE_LEAD_WEBHOOK_URL } from '@/constants/analytics'

/* ============================================================
   Helpers
   ============================================================ */

const generateEventId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch (_) { /* fallback */ }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

const readCookie = (name) => {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

const buildFbcFromFbclid = (fbclid) => {
  if (!fbclid) return undefined
  return `fb.1.${Date.now()}.${fbclid}`
}

const extractMarketingParams = () => {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const get = (k) => params.get(k) || undefined
  return {
    fbclid:        get('fbclid'),
    gclid:         get('gclid'),
    gbraid:        get('gbraid'),
    wbraid:        get('wbraid'),
    ttclid:        get('ttclid'),
    msclkid:       get('msclkid'),
    utm_source:    get('utm_source'),
    utm_medium:    get('utm_medium'),
    utm_campaign:  get('utm_campaign'),
    utm_content:   get('utm_content'),
    utm_term:      get('utm_term'),
    utm_id:        get('utm_id')
  }
}

const extractPageContext = () => {
  if (typeof window === 'undefined') return {}
  return {
    page_url:        window.location.href,
    page_path:       window.location.pathname,
    page_search:     window.location.search,
    page_title:      typeof document !== 'undefined' ? document.title : undefined,
    referrer:        typeof document !== 'undefined' ? document.referrer : undefined,
    language:        typeof navigator !== 'undefined' ? navigator.language : undefined,
    user_agent:      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    timezone:        Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone,
    viewport_width:  typeof window !== 'undefined' ? window.innerWidth  : undefined,
    viewport_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
    screen_width:    typeof screen !== 'undefined' ? screen.width  : undefined,
    screen_height:   typeof screen !== 'undefined' ? screen.height : undefined
  }
}

const buildUserData = (lead) => {
  const nomeCompleto = (lead.nome || '').trim()
  const partes = nomeCompleto.split(/\s+/)
  const firstName = lead.primeiroNome || partes[0] || ''
  const lastName  = partes.slice(1).join(' ')
  const country = (lead.pais?.code || '').toLowerCase()
  const phone = String(lead.telefoneCompleto || '').replace(/\D/g, '')

  return {
    email:      lead.email?.trim().toLowerCase() || '',
    phone,
    first_name: firstName,
    last_name:  lastName,
    country,
    fbp:        readCookie('_fbp'),
    fbc:        readCookie('_fbc') || (typeof window !== 'undefined'
                  ? buildFbcFromFbclid(new URLSearchParams(window.location.search).get('fbclid'))
                  : undefined)
  }
}

/* ============================================================
   Estratégia de envio com fallbacks
   ------------------------------------------------------------
   Por que essa complexidade:
   - sendBeacon retorna true mas pode silenciosamente não enviar
     quando o Content-Type é application/json (alguns navegadores
     tratam como CORS preflight necessário e abortam).
   - fetch keepalive é mais confiável em ambientes desktop.
   - Image beacon (GET com query string) é última instância — não
     bloqueado por adblocks comuns que filtram por url-pattern.

   Sempre logamos o caminho usado no console para debugging em prod.
   ============================================================ */

const sendViaFetch = (body) => {
  /* fetch com keepalive sobrevive a unload e respeita Content-Type.
     Usamos mode: 'no-cors' porque o Make webhook não retorna headers
     CORS — o request sai, só não conseguimos ler o response. */
  try {
    fetch(MAKE_LEAD_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'no-cors'
    })
      .then((r) => console.info('[webhookLead] fetch OK', r.type, r.status))
      .catch((e) => console.warn('[webhookLead] fetch falhou:', e?.message))
    return true
  } catch (e) {
    console.warn('[webhookLead] fetch threw:', e?.message)
    return false
  }
}

const sendViaBeacon = (body) => {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false
  try {
    /* type 'text/plain' é o único que sendBeacon entrega sem
       preflight em todos os navegadores. O Make aceita o body como
       string crua e nós parseamos no módulo "JSON → Parse JSON". */
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
    const ok = navigator.sendBeacon(MAKE_LEAD_WEBHOOK_URL, blob)
    console.info('[webhookLead] sendBeacon →', ok ? 'queued' : 'rejected')
    return ok
  } catch (e) {
    console.warn('[webhookLead] sendBeacon threw:', e?.message)
    return false
  }
}

/**
 * Image beacon — última instância. GET com payload em query string
 * (limitado a ~2000 bytes; truncamos campos opcionais se necessário).
 * Funciona mesmo quando adblockers bloqueiam fetch/sendBeacon, mas
 * tem desvantagens (sem body POST estruturado).
 */
const sendViaImage = (compactPayload) => {
  try {
    const query = encodeURIComponent(JSON.stringify(compactPayload))
    /* Se passar do limite, navegador trunca silenciosamente — por isso
       só mandamos campos essenciais nesta rota. */
    const img = new Image()
    img.referrerPolicy = 'no-referrer-when-downgrade'
    img.src = `${MAKE_LEAD_WEBHOOK_URL}?_p=${query}`
    console.info('[webhookLead] image beacon dispatched, size:', img.src.length)
    return true
  } catch (e) {
    console.warn('[webhookLead] image beacon threw:', e?.message)
    return false
  }
}

const dispatch = (payload) => {
  if (typeof window === 'undefined') return false

  const body = JSON.stringify(payload)
  console.info('[webhookLead] dispatch start, size:', body.length, 'B')

  /* Tentamos em série até alguma estratégia confirmar entrega.
     fetch é o caminho primário; sendBeacon entra como reserva
     se fetch falhar logo de cara (raríssimo, mas vale a defesa). */
  if (sendViaFetch(body)) return true
  if (sendViaBeacon(body)) return true

  /* Último recurso: image beacon com payload mínimo. */
  const compact = {
    event_id:   payload.event_id,
    event_name: payload.event_name,
    email:      payload.user_data?.email,
    phone:      payload.user_data?.phone,
    fbp:        payload.user_data?.fbp,
    fbc:        payload.user_data?.fbc,
    fbclid:     payload.marketing?.fbclid,
    url:        payload.event_source_url
  }
  return sendViaImage(compact)
}

/* ============================================================
   API pública
   ============================================================ */

export const sendLeadToWebhook = (lead = {}, meta = {}) => {
  const eventId = generateEventId()

  const payload = {
    event_name:        meta.eventName || 'Lead',
    event_id:          eventId,
    event_time:        Math.floor(Date.now() / 1000),
    event_source_url:  typeof window !== 'undefined' ? window.location.href : undefined,
    action_source:     'website',
    user_data:         buildUserData(lead),
    custom_data: {
      content_name:     meta.contentName     || 'lead_form',
      content_category: meta.contentCategory || 'lead',
      currency:         meta.currency        || 'EUR',
      value:            typeof meta.value === 'number' ? meta.value : 0,
      profession:       lead.profissao,
      lead_language:    lead.idioma
    },
    marketing: extractMarketingParams(),
    page:      extractPageContext(),
    lead_raw: {
      nome:              lead.nome,
      primeiro_nome:     lead.primeiroNome,
      email:             lead.email,
      telefone_local:    lead.telefone,
      telefone_e164:     lead.telefoneCompleto,
      profissao:         lead.profissao,
      pais_codigo:       lead.pais?.code,
      pais_nome:         lead.pais?.nome,
      idioma:            lead.idioma
    }
  }

  console.info('[webhookLead] payload pronto', { eventId, email: payload.user_data.email })
  dispatch(payload)

  return eventId
}
